/**
 * Universal Environment Variable Utilities
 * CRITICAL FIX: Strips shell script artifacts from environment variables
 * 
 * Problem: Shell scripts accidentally included `-n ` and `-e ` flags when uploading
 * to Vercel, causing these prefixes to be stored as part of the variable values.
 * 
 * Solution: This utility strips these artifacts universally across all env var access.
 */

/**
 * Get environment variable with automatic cleaning of shell artifacts
 * Handles both process.env and import.meta.env for compatibility
 * 
 * @param key - Environment variable name (with or without VITE_ prefix)
 * @returns Cleaned environment variable value or undefined
 */
export function getEnv(key: string): string | undefined {
    // Try both with and without VITE_ prefix for flexibility
    let value: string | undefined;

    // Check if running in browser (Vite) or Node.js environment
    if (typeof import.meta !== 'undefined' && import.meta.env) {
        // Browser/Vite environment
        value = import.meta.env[key] as string | undefined;
        if (!value && !key.startsWith('VITE_')) {
            // Try with VITE_ prefix
            value = import.meta.env[`VITE_${key}`] as string | undefined;
        }
    } else if (typeof process !== 'undefined' && process.env) {
        // Node.js environment (SSR, tests)
        value = process.env[key];
        if (!value && !key.startsWith('VITE_')) {
            value = process.env[`VITE_${key}`];
        }
    }

    // CRITICAL FIX: Strip shell script artifacts
    // - `-n ` prefix from `echo -n`
    // - `-e ` prefix from `echo -e`
    // - Trim whitespace
    if (value) {
        return value
            .trim()
            .replace(/^-n\s+/, '')
            .replace(/^-e\s+/, '');
    }

    return value;
}

/**
 * Get environment variable and throw if not found
 * Useful for required configuration values
 * 
 * @param key - Environment variable name
 * @param errorMessage - Custom error message (optional)
 * @returns Cleaned environment variable value
 * @throws Error if variable is not defined
 */
export function getEnvRequired(key: string, errorMessage?: string): string {
    const value = getEnv(key);
    if (!value) {
        throw new Error(
            errorMessage || `Required environment variable ${key} is not defined`
        );
    }
    return value;
}

/**
 * Get boolean environment variable
 * Handles common boolean representations
 * 
 * @param key - Environment variable name
 * @param defaultValue - Default value if not set (default: false)
 * @returns Boolean value
 */
export function getEnvBoolean(key: string, defaultValue = false): boolean {
    const value = getEnv(key);
    if (!value) return defaultValue;

    const normalized = value.toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

/**
 * Get numeric environment variable
 * 
 * @param key - Environment variable name
 * @param defaultValue - Default value if not set or invalid
 * @returns Numeric value
 */
export function getEnvNumber(key: string, defaultValue?: number): number | undefined {
    const value = getEnv(key);
    if (!value) return defaultValue;

    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
}
